import { IfListItem } from '@/types/types';
import { utils as XLSX, writeFile } from 'xlsx-js-style';

// Define color constants like in ExportButton
const COLORS = {
  GREEN: "16A085",
  RED: "E74C3C", 
  YELLOW: "F39C12",
  ORANGE: "E67E22",
  BLUE: "3498DB",
  PURPLE: "8E44AD",
  DARK: "2C3E50",
  GRAY: "7F8C8D"
};

function extractProperty(data: any, property: string, defaultValue: any) {
  return data?.[property] ?? defaultValue;
}

interface ConversionStatus {
  status: 'processing' | 'complete' | 'error' | 'warning';
  message: string;
}

export default async function convertToExcel(
  mtmtId: string, 
  year: number,
  onStatusUpdate: (status: ConversionStatus) => void
): Promise<void> {
  let iflist: IfListItem[] = [];

  try {
    onStatusUpdate({ status: 'processing', message: 'Loading Impact Factor data...' });
    const response = await fetch("/iflist.csv");
    if (!response.ok) {
      throw new Error("Failed to load impact factor data");
    }
    
    const content = await response.text();
    const rows = content.split("\n");
    const headers = rows[0].split(",").map(header => header.trim());

    onStatusUpdate({ status: 'processing', message: 'Processing Impact Factor data...' });
    iflist = rows.slice(1).map(row => {
      const values = row.split(",");
      const obj: Record<string, string> = {};
      headers.forEach((header, index) => {
        obj[header] = values[index]?.trim() || '';
      });
      return obj as unknown as IfListItem;
    });
  } catch (error) {
    onStatusUpdate({ status: 'error', message: 'Error loading Impact Factor (IF) List. Proceeding without it.' });
  }

  if (!iflist?.length) {
    onStatusUpdate({ status: 'warning', message: 'No Impact Factor (IF) List data available. Proceeding without it.' });
  }

  onStatusUpdate({ status: 'processing', message: 'Preparing Excel export...' });
  const url = `https://m2.mtmt.hu/api/publication?cond=published;eq;true&cond=core;eq;true&cond=institutes;inia;${mtmtId}&cond=publishedYear;in;${year}&sort=publishedYear,desc&sort=firstAuthor,asc&size=5000&labelLang=hun&cite_type=2&incFields=authorships,identifiers,journal,fullPublication,published,book,publishedYear,foreignEdition,foreignLanguage,title,pageLength,firstPage,lastPage,ratings,ratingsForSort,category,subType,type&format=json`;
  
  const response = await fetch(url);
  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Error fetching data: ${error}`);
  }

  const data = await response.json();
  if (!data?.content) {
    throw new Error('Invalid data format received');
  }

  onStatusUpdate({ status: 'processing', message: 'Transforming publication data' });
  
  const headers = [
    "Title", "Journal", "Fullname", "Tanszek", "Jmtid", "Type", "SubType",
    "pIssn", "eIssn", "SJR", "IF", "Quartile", "Amtid",
    "Share", "Category", "Author", "Published"
  ];

  // Define header style
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: COLORS.DARK } },
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "B2BEB5" } },
      bottom: { style: "thin", color: { rgb: "B2BEB5" } },
      left: { style: "thin", color: { rgb: "B2BEB5" } },
      right: { style: "thin", color: { rgb: "B2BEB5" } }
    }
  };

  // Define cell style
  const cellStyle = {
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "B2BEB5" } },
      bottom: { style: "thin", color: { rgb: "B2BEB5" } },
      left: { style: "thin", color: { rgb: "B2BEB5" } },
      right: { style: "thin", color: { rgb: "B2BEB5" } }
    }
  };

  // Transform data for Excel with styling
  const excelData = [
    headers.map(header => ({ v: header, s: headerStyle })),
    ...data.content.flatMap((publication: any) => 
      publication.authorships.map((auth: any) => {
        const { familyName = '', givenName = '' } = auth.author ?? {};
        const ifdata = iflist?.find(item => 
          item.year == year && 
          (item.pissn == publication.journal?.pIssn || item.eissn == publication.journal?.eIssn)
        );
        
        const rowData = {
          Title: { v: extractProperty(publication, 'label', ''), s: { ...cellStyle, font: { color: { rgb: COLORS.DARK } } } },
          Journal: { v: extractProperty(publication.journal, 'label', ''), s: { ...cellStyle, font: { color: { rgb: COLORS.BLUE } } } },
          Fullname: { v: `${familyName} ${givenName}`, s: cellStyle },
          Tanszek: { v: auth.label?.split(']')?.[1]?.trim() || '', s: cellStyle },
          Jmtid: { v: extractProperty(publication, 'mtid', ''), s: cellStyle },
          Type: { v: extractProperty(publication.type, 'label', ''), s: cellStyle },
          SubType: { v: extractProperty(publication.subType, 'label', ''), s: cellStyle },
          pIssn: { v: extractProperty(publication.journal, 'pIssn', ''), s: cellStyle },
          eIssn: { v: extractProperty(publication.journal, 'eIssn', ''), s: cellStyle },
          SJR: { v: extractProperty(publication, 'ratingsForSort', ''), s: { ...cellStyle, font: { color: { rgb: COLORS.PURPLE } } } },
          IF: { v: extractProperty(ifdata, 'if', ''), s: { ...cellStyle, font: { color: { rgb: COLORS.YELLOW } } } },
          Quartile: { v: extractProperty(ifdata, 'quartile', ''), s: { ...cellStyle, font: { color: { rgb: COLORS.GREEN } } } },
          Amtid: { v: extractProperty(auth.author, 'mtid', ''), s: cellStyle },
          Share: { v: extractProperty(auth, 'share', ''), s: { ...cellStyle, font: { color: { rgb: COLORS.ORANGE } } } },
          Category: { v: extractProperty(publication.category, 'label', ''), s: { ...cellStyle, font: { color: { rgb: COLORS.ORANGE } } } },
          Author: { v: extractProperty(auth, 'label', ''), s: cellStyle },
          Published: { v: extractProperty(publication, 'published', ''), s: { ...cellStyle, font: { color: { rgb: COLORS.GREEN } } } }
        };

        return headers.map(header => rowData[header as keyof typeof rowData]);
      })
    )
  ];

  onStatusUpdate({ status: 'processing', message: 'Creating Excel workbook...' });
  const workbook = XLSX.book_new();
  const worksheet = XLSX.aoa_to_sheet(excelData);

  onStatusUpdate({ status: 'processing', message: 'Formatting Excel data...' });
  // Enable table format with autofilter
  worksheet['!autofilter'] = { ref: worksheet['!ref'] || 'A1' };

  // Set column widths with better sizing
  const wscols = [
    { wch: 40 }, // Title
    { wch: 30 }, // Journal
    { wch: 20 }, // fullname
    { wch: 25 }, // tanszek
    { wch: 15 }, // Jmtid
    { wch: 15 }, // otype
    { wch: 12 }, // pIssn
    { wch: 12 }, // eIssn
    { wch: 15 }, // SJRranking
    { wch: 10 }, // if
    { wch: 12 }, // quartile
    { wch: 15 }, // Amtid
    { wch: 10 }, // Share
    { wch: 30 }  // Author
  ];
  worksheet['!cols'] = wscols;

  XLSX.book_append_sheet(workbook, worksheet, `MTMT_${year}`);
  
  // Generate filename
  const fileName = `MTMT_Export_${year}_${new Date().toISOString().split('T')[0]}.xlsx`;
  
  onStatusUpdate({ status: 'processing', message: 'Generating Excel file...' });
  writeFile(workbook, fileName);
  
  onStatusUpdate({ status: 'complete', message: 'File is ready for download.' });
}
